import { useId, useRef, useState, useEffect, type ReactNode } from "react";

import { Input } from "@/components/Input";
import { useDebounced } from "@/hooks/useDebounced";
import { useLocationSearch, useLookupPlace } from "@/hooks/useLocationSearch";

import { Dropdown } from "./Dropdown";
import { FormField } from "./FormField";
import { MenuItem } from "./MenuItem";

const SEARCH_DEBOUNCE_MS = 300;

export function LocationLookupInput({
  disabled,
  name,
  title,
  placeholder,
  defaultValue,
  required,
}: {
  disabled?: boolean;
  name: string;
  title?: ReactNode;
  placeholder?: string;
  defaultValue?: string;
  required?: boolean;
}) {
  const [value, setValue] = useState(defaultValue ?? "");
  const [query, setQuery] = useState("");
  const [isShowingOptions, setIsShowingOptions] = useState(false);
  const debouncedQuery = useDebounced(query, SEARCH_DEBOUNCE_MS);
  const locationSearch = useLocationSearch(debouncedQuery);
  const place = useLookupPlace(defaultValue);

  const popover = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const id = useId();

  function selectSuggestion(suggestion: google.maps.places.PlacePrediction) {
    setQuery(
      suggestion.secondaryText
        ? `${suggestion.mainText!.text} (${suggestion.secondaryText.text})`
        : suggestion.mainText!.text,
    );
    setValue(suggestion.placeId);
    setIsShowingOptions(false);
  }

  useEffect(() => {
    if (locationSearch.data?.length && isShowingOptions) {
      popover.current?.showPopover();
    } else {
      popover.current?.hidePopover();
    }
  }, [locationSearch.data, isShowingOptions]);

  // TODO: the showing/hiding of the options dropdown is still not that smooth
  const timeout = useRef<number>(undefined);
  function showOptions() {
    window.clearTimeout(timeout.current);
    timeout.current = window.setTimeout(() => setIsShowingOptions(true), 150);
  }
  function hideOptions() {
    window.clearTimeout(timeout.current);
    timeout.current = window.setTimeout(() => setIsShowingOptions(false), 150);
  }

  return (
    <>
      <FormField title={title}>
        <div className="flex flex-col gap-2">
          <Input
            type="search"
            showRequired={required}
            name={`${name}__query`}
            validationName={name}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={place.data?.displayName || placeholder}
            disabled={disabled}
            style={{ anchorName: `--${id}` }}
            ref={input}
            onFocus={showOptions}
            onBlur={hideOptions}
          />
          {locationSearch.isSuccess && (
            <Dropdown
              popover="auto"
              className="absolute -ml-2 w-max min-w-60 [position-area:bottom_span-right]"
              style={{ positionAnchor: `--${id}` }}
              ref={popover}
            >
              {locationSearch.data?.map((suggestion) => (
                <MenuItem key={suggestion.placeId} onClick={() => selectSuggestion(suggestion)}>
                  <div className="flex flex-col items-start gap-0">
                    <p className="font-interest">{suggestion.mainText?.text}</p>
                    <p className="font-hint text-placeholder empty:hidden">{suggestion.secondaryText?.text}</p>
                  </div>
                </MenuItem>
              ))}
            </Dropdown>
          )}
        </div>
      </FormField>
      <input type="hidden" required={required} name={name} value={value} />
    </>
  );
}
